import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fromGel } from "../config/currency";
import "./ListingDetail.css";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import HomeButton from "../components/HomeButton";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { getPriceScale } from "../services/priceScale";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AMENITIES_GROUPS = {
  about_locality: [
    "near_to_metro","near_to_public_transport","near_to_public_school",
    "near_to_kindergarten","near_to_recreation_area","near_to_market",
    "near_to_pharmacy","near_to_hospital","near_to_police_station",
    "near_to_sports_space","near_to_green_side","near_to_touristic_site",
    "near_to_square","near_to_park","near_to_forest","near_to_city_waters"
  ],

  building_facilities: [
    "private_parking","private_yard","private_recreation","rooftop_recreation",
    "elevator","wheelchair_accessible","free_wifi","security_service",
    "package_lockers","surveillance_camera","green_space","fitness_centre",
    "swimming_pool","spa","business_space","bike_storage","garage","baseeement"
  ],

  private_views: [
    "city_view","mountain_view","water_view","greenside_view","historic_site_view"
  ],

  technical_equip: [
    "television","internet","central_heating","cooling_system","stove","fridge",
    "microwave","dish_washer","hood","washing_machine","tumble_dryer","gas",
    "electricity","warm_water","cold_water","conditioner","priv_surveillance_camera",
    "smart_home"
  ],

  inventories: [
    "ready_to_live","furniture","household_items","decoration",
    "plant_friendly","pet_friendly","exotic_pet_friendly"
  ],

  balcony: [
    "balcony","french_balcony","closed_balcony","loggia",
    "terrace","closed_terrace","rooftop","porch"
  ]
};

export default function ListingDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
const location = useLocation();

const fromUploadSuccess = location.state?.fromUploadSuccess;

const [currency, setCurrency] = React.useState(
  localStorage.getItem("currency") || "GEL"
);

const changeCurrency = (c) => {
  localStorage.setItem("currency", c);
  setCurrency(c);
};

const toggleCurrency = () => {
  const next = currency === "GEL" ? "USD" : "GEL";
  changeCurrency(next);
};

  const [photoIndex, setPhotoIndex] = React.useState(0);
const [lightboxOpen, setLightboxOpen] = React.useState(false);
const [listing, setListing] = React.useState(null);

const normalizedRooms = React.useMemo(() => {
  if (!listing?.rooms) return null;

  if (listing.rooms === "10+") return 10;

  const num = Number(listing.rooms);
  if (isNaN(num)) return null;

  return Math.min(10, num);
}, [listing]);


const [priceScale, setPriceScale] = React.useState(null);


const [loading, setLoading] = React.useState(true);
const [ownerUser, setOwnerUser] = React.useState(null);
 const [isFavorite, setIsFavorite] = React.useState(false);

const [currentUser, setCurrentUser] = React.useState(null);


React.useEffect(() => {
  if (!currentUser) return;
  const checkFavorite = async () => {
    const { collection, query, where, getDocs } = await import("firebase/firestore");
    const q = query(
      collection(db, "favorites"),
      where("userId", "==", currentUser.uid),
      where("listingId", "==", id)
    );
    const snap = await getDocs(q);
    setIsFavorite(!snap.empty);
  };
  checkFavorite();
}, [currentUser, id]);

const handleFavoriteToggle = async () => {
  if (!currentUser) {
    navigate("/signin", { state: { from: `/listing/${id}` } });
    return;
  }
  try {
    const { collection, addDoc, query, where, getDocs, deleteDoc, doc } = await import("firebase/firestore");
    const favCollection = collection(db, "favorites");
    const q = query(
      favCollection,
      where("userId", "==", currentUser.uid),
      where("listingId", "==", id)
    );
    const snap = await getDocs(q);

    if (!snap.empty) {
      // remove from favorites
      await deleteDoc(doc(db, "favorites", snap.docs[0].id));
      setIsFavorite(false);
    } else {
      // add to favorites
      await addDoc(favCollection, {
        userId: currentUser.uid,
        listingId: id,
        createdAt: new Date(),
      });
      setIsFavorite(true);
    }
  } catch (err) {
    console.error(err);
    alert("Failed");
  }
};


React.useEffect(() => {
  const auth = getAuth();

  const unsubscribe = auth.onAuthStateChanged((user) => {
    setCurrentUser(user);
  });

  return () => unsubscribe();
}, []);
const isOwner =
  currentUser &&
  (currentUser.uid === listing?.userId ||
   currentUser.uid === listing?.userID);


const handleDeleteListing = async () => {
  const confirmDelete = window.confirm("Delete this listing?");
  if (!confirmDelete) return;

  try {
    await deleteDoc(doc(db, "listings", id));

    alert("Listing deleted");
    navigate("/");
  } catch (err) {
    console.error(err);
    alert("Failed to delete listing");
  }
};


React.useEffect(() => {
  const fetchListing = async () => {
    try {
      const docRef = doc(db, "listings", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setListing({ id: docSnap.id, ...docSnap.data() });
      } else {
        setListing(null);
      }
    } catch (err) {
      console.error("Error fetching listing:", err);
      setListing(null);
    } finally {
      setLoading(false);
    }
  };


  fetchListing();
}, [id]);


const handleBack = () => {
  if (location.state?.from) {
    navigate(location.state.from);
    return;
  }

  navigate(-1);
};


React.useEffect(() => {
  if (!listing) return;

  const fetchOwner = async () => {
    try {
      const userRef = doc(db, "publicUsers", listing.userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setOwnerUser(userSnap.data());
      }
    } catch (err) {
      console.error("Failed to fetch listing owner:", err);
    }
  };

  fetchOwner();
}, [listing]);



React.useEffect(() => {
  console.log("========== PRICE SCALE TEST ==========");

  console.log("1. LISTING:", listing);

  if (!listing) {
    console.log("2. NO LISTING");
    return;
  }

  console.log("2. TYPE:", listing.type);
  console.log("3. CITY:", listing.city);
  console.log("4. PURPOSE:", listing.purpose);
  console.log("5. PRICE:", listing.price_gel);
  console.log("6. AREA:", listing.area);

  const test = async () => {
    try {
      console.log("7. CALLING getPriceScale...");

      const result = await getPriceScale(listing);

      console.log("8. getPriceScale RETURNED:", result);

      setPriceScale(result);
    } catch (error) {
      console.error("9. getPriceScale ERROR:", error);
    }
  };

  test();
}, [listing]);



if (loading) return <p>Loading...</p>;
if (!listing) return <p>{t("listing.not_found")}</p>;

  return (
    <div className="listing-page">
      
   
<div className="top-bar">

  {fromUploadSuccess ? (
    <HomeButton />
  ) : (
    <button
      className="back-btn"
      onClick={handleBack}
    >
      ← {t("common.back")}
    </button>
  )}

  <div className="top-right">
    <button
      onClick={handleFavoriteToggle}
      className={`favorite-btn top-favorite ${isFavorite ? "added" : ""}`}
    >
      {isFavorite ? "❤️" : "🤍"}
    </button>
  </div>

</div>



      {/* HEADER */}
      <header className="listing-header">
        <h1>
    {t(`property.type.${listing.type}`)}
    {" · "}
    {t(`property.purpose.${listing.purpose}`)}
  </h1>


<div className="location-row">
        <p className="location">
  {listing.city && t(`location.${listing.city}`)}
  {listing.neighborhood && `, ${t(`location.${listing.neighborhood}`)}`}
  {listing.localarea && `, ${t(`location.${listing.localarea}`)}`}
        </p>

<a href={`tel:${listing.phone}`} className="call-btn-top">
  📞 {t("call_now")}
</a>
</div>

  <div className="price-row">

<button className="currency-toggle" onClick={toggleCurrency}>
  {currency === "GEL" ? "₾ GEL" : "$ USD"}
</button>

<span className="price">
  {currency === "USD" && "$"}
  {currency === "GEL" && "₾"}
  {fromGel(listing.price_gel, currency).toLocaleString()}
</span>

</div>

{listing.openForForeign && (
    <span className="badge">{t("listing.openForForeign")}</span>
  )}

  {listing.openForAgents && (
    <span className="badge">{t("listing.openForAgents")}</span>
  )}

  {listing.priceNegotiable && (
    <span className="badge">{t("leasting.priceNegotiable")}</span>
  )}

  {listing.bankPayment && (
    <span className="badge">{t("leasting.bankPayment")}</span>
  )}

  {listing.exchangePossible && (
    <span className="badge">{t("leasting.exchangePossible")}</span>
  )}

     </header>

      {/* PHOTOS */}
   {/* PHOTOS */}
<div className="gallery-with-price-scale">

  {priceScale && (
    <div className="price-scale">

      <span className="price-scale-label top">
        {t("priceScale.high")}
      </span>

      <div className="price-scale-track">
        <div
          className="price-scale-marker"
          style={{
            top: `${100 - priceScale.percentile}%`
          }}
        >
          <span className="price-scale-dot"></span>
        </div>
      </div>

      <span className="price-scale-label bottom">
        {t("priceScale.low")}
      </span>

    </div>
  )}

  <div className="gallery">
  {/* MAIN IMAGE (clickable to open lightbox) */}
  {listing.photos?.length > 0 && !lightboxOpen && (
    <>
      <img
        src={listing.photos[photoIndex]}
        alt="Listing"
        className="listing-image"
        onClick={() => setLightboxOpen(true)}
      />

      <div className="gallery-dots">
        {listing.photos.map((_, i) => (
          <span
            key={i}
            className={i === photoIndex ? "active" : ""}
            onClick={() => setPhotoIndex(i)}
          />
        ))}
      </div>
    </>
  )}

  {/* LIGHTBOX */}
  {lightboxOpen && (
    <div className="lightbox">
      {/* CLOSE BUTTON */}
      <button
        className="lightbox-close"
        onClick={() => setLightboxOpen(false)}
      >
        ✕
      </button>

      {/* FULL IMAGE */}
      <img
        src={listing.photos[photoIndex]}
        alt="Full"
        className="lightbox-image"
      />

      {/* LEFT ARROW */}
      {photoIndex > 0 && (
        <div
          className="gallery-arrow left"
          onClick={() => setPhotoIndex(photoIndex - 1)}
        >
          ‹
        </div>
      )}

      {/* RIGHT ARROW */}
      {photoIndex < listing.photos.length - 1 && (
        <div
          className="gallery-arrow right"
          onClick={() => setPhotoIndex(photoIndex + 1)}
        >
          ›
        </div>
      )}

      {/* DOTS NAVIGATION */}
      <div className="gallery-dots lightbox-dots">
        {listing.photos.map((_, i) => (
          <span
            key={i}
            className={i === photoIndex ? "active" : ""}
            onClick={() => setPhotoIndex(i)}
          />
        ))}
      </div>
    </div>
   )}
 </div>
</div>

      {/* MAIN DETAILS */}
      <section className="details-grid">
<Detail
  label={t("common.subtype")}
  value={
  listing.subType
    ? (Array.isArray(listing.subType)
        ? listing.subType
        : [listing.subType]
      )
        .map(s => t(`property.subtype.${s}`))
        .join(", ")
    : null
}
/>
  <Detail label={t("common.area")} value={`${listing.area} ${t("common.m²")}`} />

<Detail
  label={t("yard_area")}
  value={
    listing.yardArea
      ? `${listing.yardArea} ${t("common.m²")}`
      : null
  }
/>

 <Detail
  label={t("room_type")}
  value={
    normalizedRooms == null
      ? null
      : normalizedRooms >= 10
        ? "10+"
        : normalizedRooms
  }
/>

  <Detail label={t("bedroom_type")} value={listing.bedrooms} />
  
  <Detail label={t("bathroom_type")} value={listing.bathrooms} />

  <Detail
  label={t("floor_type")}
  value={
    listing.floor && listing.totalFloors
      ? `${listing.floor} / ${listing.totalFloors}`
      : listing.floor
  }
 />

<Detail
  label={t("flo.totalFloors")}
  value={listing.totalFloors}
/>

  {!(
  listing.condition === "green_frame" &&
  (listing.renovation === "renovation_new" ||
   listing.renovation === "renovation_old")
) && (
  <Detail
    label={t("condition")}
    value={t(listing.condition)}
  />
)}

<Detail
  label={t("renovation")}
  value={listing.renovation && t(listing.renovation)}
/>

  <Detail
    label={t("building")}
    value={t(listing.building)}
  />

  <Detail
    label={t("project_type")}
    value={listing.projectType && t(listing.projectType)}
  />

</section>

 {/* DESCRIPTION */}
      <section>
        <h3>{t("propertaa.description")}</h3>

  <p className="listing-details-description">
    {listing.description}
  </p>

      </section>


      {/* AMENITIES */}
      {listing.amenities?.length > 0 && (
  <section>
    <h3>{t("propertaa.amenities")}</h3>

    {Object.entries(AMENITIES_GROUPS).map(([group, items]) => {

      const selected = items.filter(i =>
        listing.amenities.includes(i)
      );

      if (selected.length === 0) return null;

      return (
        <div key={group} className="amenities-group">
          <h4>{t(`amenities.group.${group}`)}</h4>

          <ul className="amenities">
            {selected.map(item => (
              <li key={item}>
                {t(`amenities.items.${item}`)}
              </li>
            ))}
          </ul>
        </div>
      );
    })}

  </section>
)}


{listing.street && (
  <p className="listing-street">
    📍 {listing.street[i18n.language] || listing.street.ka} {listing.streetNumber || ""}
  </p>
)}



{/* LOCATION (MAP) */}
{listing.lat && listing.lng && (
  <section className="location-full">
    <h3>{t("propertaa.location")}</h3>


    <div style={{ height: 300, marginTop: 12 }}>
      <MapContainer
        center={[listing.lat, listing.lng]}
        zoom={16}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <Marker position={[listing.lat, listing.lng]} />
      </MapContainer>
    </div>

{listing.cadastralCode && (
  <p style={{ marginTop: 10 }}>
    <strong>{t("propertaa.cadastralCode")}:</strong> {listing.cadastralCode}
  </p>

)}

  </section>
)}

   {/* CONTACT */}
<section className="contact">

<div className="seller-card">

<div className="seller-info">
<span className="seller-avatar">👤</span>

<div className="seller-text">
<strong className="seller-name">
{ownerUser?.username || listing.username || "User"}
</strong>

<span
className="seller-listings-link"
onClick={() => navigate(`/user/${listing.userId}`)}
>
{t("listing.viewAllFromUser")}
</span>
</div>
</div>

</div>

{isOwner && (
  <div className="owner-actions">

    {/* EDIT BUTTON */}
    <button
      onClick={() => navigate("/add-listing", { state: { listing } })}
      style={{
        marginTop: 10,
        marginRight: 10,
        background: "#2563eb",
        color: "white",
        border: "none",
        padding: "8px 12px",
        borderRadius: 6,
        cursor: "pointer"
      }}
    >
      {t("edit_listing")}
    </button>

    {/* DELETE BUTTON */}
    <button
      onClick={handleDeleteListing}
      style={{
        marginTop: 10,
        background: "red",
        color: "white",
        border: "none",
        padding: "8px 12px",
        borderRadius: 6,
        cursor: "pointer"
      }}
    >
      {t("delete_Listing")}
    </button>

  </div>
)}

</section>
</div>
  );
}

function Detail({ label, value }) {
   if (value === null || value === undefined || value === "") return null;
  return (
    <div className="detail">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
